import { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Loader2, Play, Pause, Check, X } from 'lucide-react';
import { TextToSpeechService } from '@/services/integrations/elevenlabs/TextToSpeechService';
import { Voice } from '@/types/voice/voice';
import { getGenieImage } from '@/utils/image/genieAvatars';
import { toast } from 'sonner';

interface VoiceComparisonPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  voices: Voice[];
  onSelectVoice?: (voiceId: string) => void;
  onRemoveVoice: (voiceId: string) => void;
  defaultText?: string;
}

interface VoiceAudio {
  voiceId: string;
  audioUrl: string | null;
  isLoading: boolean;
  isPlaying: boolean;
}

export const VoiceComparisonPanel = ({
  open,
  onOpenChange,
  voices,
  onSelectVoice,
  onRemoveVoice,
  defaultText = "Hey, what do you think about my voice? Do you think I could be one of your genies?"
}: VoiceComparisonPanelProps) => {
  const [text, setText] = useState(defaultText);
  const [voiceAudios, setVoiceAudios] = useState<VoiceAudio[]>([]);
  const audioRefs = useRef<Map<string, HTMLAudioElement>>(new Map());

  useEffect(() => {
    setVoiceAudios(voices.map(v => ({
      voiceId: v.id,
      audioUrl: null,
      isLoading: false,
      isPlaying: false
    })));
  }, [voices]);

  const handleGenerateAll = async () => {
    if (!text.trim()) return;

    for (const voice of voices) {
      setVoiceAudios(prev => prev.map(va =>
        va.voiceId === voice.id ? { ...va, isLoading: true } : va
      ));

      try {
        const url = await TextToSpeechService.synthesize({ voiceId: voice.id, text });
        setVoiceAudios(prev => prev.map(va =>
          va.voiceId === voice.id ? { ...va, audioUrl: url, isLoading: false } : va
        ));
      } catch (error) {
        console.error(`Failed to generate audio for ${voice.name}:`, error);
        setVoiceAudios(prev => prev.map(va =>
          va.voiceId === voice.id ? { ...va, isLoading: false } : va
        ));
      }
    }
  };

  const togglePlayPause = (voiceId: string) => {
    const audio = audioRefs.current.get(voiceId);
    if (!audio) return;

    const voiceAudio = voiceAudios.find(va => va.voiceId === voiceId);
    if (!voiceAudio) return;

    if (voiceAudio.isPlaying) {
      audio.pause();
      setVoiceAudios(prev => prev.map(va =>
        va.voiceId === voiceId ? { ...va, isPlaying: false } : va
      ));
    } else {
      // Pause all other voices
      audioRefs.current.forEach((a, id) => {
        if (id !== voiceId) {
          a.pause();
        }
      });
      setVoiceAudios(prev => prev.map(va => ({ ...va, isPlaying: false })));

      // Play this voice
      audio.play();
      setVoiceAudios(prev => prev.map(va =>
        va.voiceId === voiceId ? { ...va, isPlaying: true } : va
      ));
    }
  };

  const handleAudioEnded = (voiceId: string) => {
    setVoiceAudios(prev => prev.map(va =>
      va.voiceId === voiceId ? { ...va, isPlaying: false } : va
    ));
  };

  const handleSelectVoice = (voiceId: string) => {
    if (onSelectVoice) {
      const voice = voices.find(v => v.id === voiceId);
      onSelectVoice(voiceId);
      toast.success(`Voice selected: ${voice?.name}`);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Compare Voices</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Test Text</label>
            <Textarea
              rows={3}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type your sample text..."
              className="resize-none"
            />
            <Button
              onClick={handleGenerateAll}
              disabled={voices.length === 0}
              className="mt-2"
            >
              <Play className="h-4 w-4 mr-2" />
              Generate All Samples
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {voices.map(voice => {
              const voiceAudio = voiceAudios.find(va => va.voiceId === voice.id);
              if (!voiceAudio) return null;

              return (
                <div
                  key={voice.id}
                  className="relative border rounded-lg p-4 bg-white hover:shadow-md transition-shadow"
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemoveVoice(voice.id)}
                    className="absolute top-2 right-2 h-6 w-6 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>

                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10">
                      <img
                        src={getGenieImage(voice.id, voice.labels?.gender as 'male' | 'female' | 'neutral' || 'neutral')}
                        alt={voice.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.src = '/helpgenie_logo.png';
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm truncate">{voice.name}</h3>
                      {voice.labels?.accent && (
                        <p className="text-xs text-gray-500 truncate">{voice.labels.accent}</p>
                      )}
                    </div>
                  </div>

                  {voice.labels && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {voice.labels.gender && (
                        <Badge variant="outline" className="text-xs">{voice.labels.gender}</Badge>
                      )}
                      {voice.labels.age && (
                        <Badge variant="outline" className="text-xs">{voice.labels.age}</Badge>
                      )}
                    </div>
                  )}

                  {voiceAudio.audioUrl && (
                    <>
                      <audio
                        ref={el => {
                          if (el) audioRefs.current.set(voice.id, el);
                        }}
                        src={voiceAudio.audioUrl}
                        onEnded={() => handleAudioEnded(voice.id)}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => togglePlayPause(voice.id)}
                        className="w-full mb-2"
                      >
                        {voiceAudio.isPlaying ? (
                          <>
                            <Pause className="h-3 w-3 mr-1" />
                            Pause
                          </>
                        ) : (
                          <>
                            <Play className="h-3 w-3 mr-1" />
                            Play
                          </>
                        )}
                      </Button>
                    </>
                  )}

                  {voiceAudio.isLoading && (
                    <div className="flex items-center justify-center py-2">
                      <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                    </div>
                  )}

                  {onSelectVoice && (
                    <Button
                      size="sm"
                      onClick={() => handleSelectVoice(voice.id)}
                      className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
                    >
                      <Check className="h-3 w-3 mr-1" />
                      Select
                    </Button>
                  )}
                </div>
              );
            })}
          </div>

          {voices.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No voices selected for comparison. Add voices from the dropdown.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
